import { typographyTokens } from '../../theme/typographyTokens';
import React, { useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Link } from 'react-router-dom';
import { Task } from '../../../shared/types/Task';
import { Department } from '../../../shared/types/Department';
import { CommentExtended } from '../../../shared/types/Comment';
import { AttachmentExtended } from '../../../shared/types/Attachment';
import StatusCell from '../SepTable/StatusCell';
import { useTheme } from '@mui/material/styles';
import AttachmentListIcon from '../../assets/svg/AttachmentListIcon';
import CommentListIcon from '../../assets/svg/CommentListIcon';

interface IconCellProps {
  iconComponent: React.ReactNode;
  value: string;
}

export const IconCell = ({ iconComponent, value }: IconCellProps) => {
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
      {iconComponent}
      {value}
    </Box>
  );
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    color: theme.palette.gray.light,
    fontSize: typographyTokens.fontSizes.body,
    paddingBottom: 4,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: typographyTokens.fontSizes.body,
  },
}));

const StyledLink = styled(Link)(({ theme }) => ({
  textDecoration: 'none',
  color: theme.palette.gray.main,
  '&:hover': {
    color: theme.palette.solidBlue.main,
  },
}));

const DependentTasksTable = ({
  task,
  departments,
  comments,
  attachments,
}: {
  task: Task[];
  departments: Department[];
  comments: CommentExtended[];
  attachments: AttachmentExtended[];
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  return (
    <Box sx={{ m: 2, maxHeight: '500px', overflow: 'auto' }}>
      <TableContainer>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <TableHead>
            <TableRow>
              <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>Task</StyledTableCell>
              <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>Department</StyledTableCell>
              <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>Status</StyledTableCell>
              <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }} colSpan={2}>Activity</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {task.map((t, index) => (
              <TableRow key={index}>
                <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                  <StyledLink
                    sx={{ fontSize: typographyTokens.fontSizes.body }}
                    className="blue-text"
                    to={`/sep/${t.sepID}/task/${t.id}`}
                  >
                    {t.name}
                  </StyledLink>
                </StyledTableCell>
                <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                  {
                    departments.filter(
                      (department) => department.id === t.departmentID
                    )[0].name
                  }
                </StyledTableCell>
                <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                  <StatusCell status={t.status} />
                </StyledTableCell>
                <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default, pr: 0 }}>
                  <IconCell
                    iconComponent={<CommentListIcon color={theme.palette.prime.blue} />}
                    value={comments
                      .filter((comment) => comment.taskID === t.id)
                      .length.toString()}
                  />
                </StyledTableCell>
                <StyledTableCell sx={{ color: isDarkMode ? theme.palette.content.white : theme.palette.content.default }}>
                  <IconCell
                    iconComponent={<AttachmentListIcon color={theme.palette.prime.blue} />}
                    value={attachments
                      .filter((attachment) => attachment.taskID === t.id)
                      .length.toString()}
                  />
                </StyledTableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default DependentTasksTable;
